import type { Fact } from "~/components/inside/facts";
import type { CapabilityId } from "~/lib/constants/capabilities";
import type { FaqItem } from "~/lib/constants/faq";
import type { SheetArt } from "~/lib/constants/sheet-art";

/**
 * The URL slugs of the dedicated service pages. English in both locales: the
 * Latvian page lives at /lv/services/<same slug>, and the slug is a search
 * term in its own right.
 */
export const SERVICE_PAGE_SLUGS = [
  "ai-agent-development",
  "rag-development",
  "n8n-automation",
  "ai-integration",
  "ai-research-agents",
] as const;

export type ServicePageSlug = (typeof SERVICE_PAGE_SLUGS)[number];

/**
 * One dedicated service page. Each one targets a single cluster of buyer
 * searches and answers it with the same parts the /services bands use: an
 * illustrative artifact, what it covers, how it is built, where it stops, and
 * the questions people ask before a call.
 */
export interface ServicePage {
  art: SheetArt;
  /** The caption under the artifact saying it is illustrative. */
  artNote: string;
  /** The /services band this page expands on. */
  capabilityId: CapabilityId;
  cta: { lede: string; title: string };
  /** Rendered on the page and mirrored in its FAQPage schema. */
  faq: FaqItem[];
  intro: string;
  /** Meta keywords. Ignored by Google, still read by some engines. */
  keywords: string;
  label: string;
  limits: { body: string; items: Fact[]; title: string };
  meta: { description: string; title: string };
  /** The short name used in navigation, links and the breadcrumb. */
  nav: string;
  scope: { body: string; points: string[]; title: string };
  /** The schema.org `serviceType`: a search category, so English. */
  serviceType: string;
  slug: ServicePageSlug;
  steps: { body: string; items: string[]; title: string };
  /** Two lines; the design breaks each headline deliberately. */
  title: [string, string];
  uses: Fact[];
}
